import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import ArticleImageBlock from '@app/components/Guide/article/ArticleImageBlock'
import {
  ArticleTable,
  ArticleTableBody,
  ArticleTableCell,
  ArticleTableHead,
  ArticleTableHeader,
  ArticleTableRow,
} from '@app/components/Guide/article/ArticleTable'
import ArticleTableLightbox from '@app/components/Guide/article/ArticleTableLightbox'
import type { Components } from 'react-markdown'
import ReactMarkdown from 'react-markdown'
import slugify from 'react-slugify'
import rehypeKatex from 'rehype-katex'
import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'
import { GlossaryPopover } from './GlossaryPopover'
import { ReactNode } from 'react'

type GuideMarkdownTailwindProps = {
  markdown: string
}

type CodeProps = React.ComponentProps<'code'> & {
  inline?: boolean
}

const getPlainText = (node: ReactNode): string => {
  if (node == null || typeof node === 'boolean') return ''
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(getPlainText).join('')
  if (typeof node === 'object' && 'props' in node) {
    return getPlainText((node as { props?: { children?: ReactNode } }).props?.children)
  }
  return ''
}

const getGlossaryTerm = (rawHref: string, children: React.ReactNode) => {
  let term = ''

  try {
    term = new URL(rawHref).hash.replace('#', '').trim()
  } catch {
    term = rawHref.split('#')[1]?.trim() ?? ''
  }

  return term || String(children)
}

const isGlossaryLink = (href: unknown) => {
  const rawHref = typeof href === 'string' ? href : ''

  return rawHref.includes('/app/guide/glossary') || rawHref.includes('/glossary')
}

const markdownComponents: Components = {
  code: (props: CodeProps) => {
    const { inline, children, ...rest } = props

    if (inline) {
      return (
        <code
          className="rounded-md bg-muted px-1.5 py-0.5 font-mono text-sm text-foreground"
          {...rest}
        >
          {children}
        </code>
      )
    }

    return (
      <ScrollArea className="w-full my-6 border rounded-lg bg-muted">
        <pre className="min-w-0 p-4 font-mono text-sm text-foreground">
          <code className="block break-words whitespace-pre-wrap" {...rest}>
            {children}
          </code>
        </pre>

        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    )
  },

  img: ({ src, alt, title }) => <ArticleImageBlock src={src} alt={alt} title={title} />,

  h1: ({ children }) => (
    <h1 className="mt-10 text-3xl font-semibold tracking-tight scroll-m-20 text-balance text-foreground">
      {children}
    </h1>
  ),

  h2: ({ children }) => {
    const id = slugify(getPlainText(children))

    return (
      <h2
        id={id}
        className="mt-10 text-2xl font-semibold tracking-tight scroll-m-20 text-balance text-foreground"
      >
        {children}
      </h2>
    )
  },

  h3: ({ children }) => {
    const id = slugify(getPlainText(children))

    return (
      <h3 id={id} className="text-xl font-semibold tracking-tight scroll-m-20 text-foreground">
        {children}
      </h3>
    )
  },

  h4: ({ children }) => {
    const id = slugify(getPlainText(children))

    return (
      <h4
        id={id}
        className="mt-6 text-base font-semibold tracking-tight scroll-m-20 text-foreground"
      >
        {children}
      </h4>
    )
  },
  h5: ({ children }) => {
    const id = slugify(getPlainText(children))
    return (
      <h5 id={id} className="mt-6 text-sm font-semibold tracking-tight scroll-m-20 text-foreground">
        {children}
      </h5>
    )
  },
  h6: ({ children }) => {
    const id = slugify(getPlainText(children))
    return (
      <h6 id={id} className="mt-6 text-xs font-semibold tracking-tight scroll-m-20 text-foreground">
        {children}
      </h6>
    )
  },

  p: ({ children }) => <p className="mt-2 leading-7 text-muted-foreground">{children}</p>,

  ul: ({ children }) => (
    <ul className="my-6 ml-6 space-y-2 list-disc text-muted-foreground">{children}</ul>
  ),

  ol: ({ children }) => (
    <ol className="my-6 ml-6 space-y-2 list-decimal text-muted-foreground">{children}</ol>
  ),

  li: ({ children }) => <li className="pl-1 leading-7">{children}</li>,

  blockquote: ({ children }) => (
    <blockquote className="py-3 pl-6 pr-4 my-6 border-l-4 border-primary bg-muted/50 text-foreground">
      {children}
    </blockquote>
  ),

  table: ({ children }) => (
    <ArticleTableLightbox>
      <ArticleTable>{children}</ArticleTable>
    </ArticleTableLightbox>
  ),

  thead: ({ children }) => <ArticleTableHead>{children}</ArticleTableHead>,

  tbody: ({ children }) => <ArticleTableBody>{children}</ArticleTableBody>,

  tr: ({ children }) => <ArticleTableRow>{children}</ArticleTableRow>,

  th: ({ children }) => <ArticleTableHeader>{children}</ArticleTableHeader>,

  td: ({ children }) => <ArticleTableCell>{children}</ArticleTableCell>,

  a: ({ children, href }) => {
    const rawHref = typeof href === 'string' ? href : ''

    if (isGlossaryLink(rawHref)) {
      return <GlossaryPopover term={getGlossaryTerm(rawHref, children)}>{children}</GlossaryPopover>
    }

    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-primary underline-offset-4 hover:underline"
      >
        {children}
      </a>
    )
  },

  br: () => <br />,
  hr: () => <Separator className="my-8" />,

  strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,

  em: ({ children }) => <em className="italic">{children}</em>,
}

export const GuideMarkdownTailwind = ({ markdown }: GuideMarkdownTailwindProps) => {
  return (
    <article className="max-w-3xl px-6 pt-1 pb-10 mx-auto text-base text-muted-foreground">
      <style>
        {`

          .katex-display {

            margin: 16px 0;

            padding: 8px 12px;

            background: hsl(var(--muted));

            border-radius: 0.5rem;

            text-align: left;

            overflow-x: auto;

          }

          .katex-inline {

            padding: 0 0.2em;

          }

          .katex {

            font-size: 1.1em;

            color: hsl(var(--foreground));

          }

        `}
      </style>

      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeRaw, rehypeKatex]}
        components={markdownComponents}
      >
        {markdown}
      </ReactMarkdown>
    </article>
  )
}
